// src/lib/v2MetricView.ts
//
// Turns one v2 `MetricResultDto` into what a supporting panel shows: a single
// figure, a labelled list, or a table — with every key named in Spanish
// (v2Vocabulary), money in euros rather than cents, durations in human units
// and shares as percentages. The panel (MetricPanelSection) only lays the
// result out, and the CSV export reads the same result as text, so the screen
// and the file never disagree.
//
// Value shapes (roommatik.analytics `V2ChainOpsMetricDefinitions`,
// `V2BoardFinItMetricDefinitions`, `V2FitMetricEngine`): a scalar (count,
// 0-1 share, `TimeSpan` string); a flat breakdown `{ key: number }` whose key
// may be composed (`checkin/kiosk`, `payment/abandoned_here`); a breakdown of
// money totals `{ purpose: { count, amount_minor } }`; a composed value with
// named fields (`{ counts: {...}, completed_share }`); a per-step entry
// `{ median_seconds, p90_seconds, n }` / `{ rate, n }` / `{ remedy, n }`; or a
// list of row objects.
import type { TranslateFunction } from 'react-admin';
import { DASH, formatCount, formatMinorMoney } from './analyticsFormat';
import { formatV2Cell, formatV2Duration, formatV2Share, isShareMetric, isV2DurationString } from './analyticsV2Format';
import { compositeTermLabel, fieldLabel, isErrorCode, stepKindRank, STEP_KIND_ORDER, termLabel } from './v2Vocabulary';
import { DateTimeSystem } from '@/utils/dateTimeSystem';
import type { MetricResultDto, MetricStampDto } from '@/types/analyticsV2';

export interface MetricListItem {
  key: string;
  label: string;
  /** Formatted value; absent when the item only groups `children`. */
  value?: string;
  children?: MetricListItem[];
}

export interface MetricTableColumn {
  key: string;
  label: string;
  align: 'left' | 'right';
}

export interface MetricTableRow {
  key: string;
  cells: Record<string, string>;
}

export type MetricView =
  | { kind: 'scalar'; text: string }
  | { kind: 'list'; items: MetricListItem[] }
  | { kind: 'table'; columns: MetricTableColumn[]; rows: MetricTableRow[] };

type Plain = Record<string, unknown>;

const isPlainObject = (value: unknown): value is Plain =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isMoneyTotal = (value: unknown): value is Plain =>
  isPlainObject(value) && typeof value.count === 'number' && typeof value.amount_minor === 'number';

const isStepEntry = (value: unknown): value is Plain =>
  isPlainObject(value) && ('median_seconds' in value || 'rate' in value || 'remedy' in value);

const isIsoDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);

/** A bare vocabulary word (`link_closed`, `up`), as opposed to an id or a name. */
const isVocabularyWord = (value: string): boolean => /^[a-z][a-z0-9_]*$/.test(value);

/** A language code (`fit.language_share`, a survey answer) by its name in the reader's language. */
export const languageName = (code: string, locale: string): string | null => {
  if (!/^[a-z]{2,3}(-[A-Z]{2})?$/.test(code)) return null;
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(code);
    return name && name !== code ? name.charAt(0).toUpperCase() + name.slice(1) : null;
  } catch {
    return null;
  }
};

const MISSING = '\u0000';

/** Named field (`count`, `completed_share`) when it is one, otherwise a vocabulary key. */
const entryLabel = (metricId: string, key: string, translate: TranslateFunction, locale: string): string => {
  if (metricId === 'fit.language_share') return languageName(key, locale) ?? key.toUpperCase();
  // A currency code (the per-currency split of a money total) reads as itself.
  if (/^[A-Z]{3}$/.test(key)) return key;
  const field = translate(`resources.insightV2.fields.${key}`, { _: MISSING });
  return field !== MISSING ? field : compositeTermLabel(key, translate);
};

/** One scalar inside a composed value, formatted by what its field name says it is. */
const formatField = (metricId: string, key: string, value: unknown, translate: TranslateFunction, currency?: string): string => {
  if (value === null || value === undefined) return DASH;
  if (typeof value === 'boolean') return translate(`resources.insightV2.breakdown.${value ? 'yes' : 'no'}`);
  if (typeof value === 'number') {
    if (/amount_minor$/.test(key)) return formatMinorMoney(value, currency);
    if (/_share$|^rate$/.test(key) || isShareMetric(metricId)) return formatV2Share(value);
    if (/_seconds$/.test(key)) return formatV2Duration(value);
    return formatCount(value);
  }
  if (typeof value === 'string') {
    if (isV2DurationString(value)) return formatV2Duration(value);
    if (isIsoDate(value)) return DateTimeSystem.formatForDisplay(value) || value;
    if (isVocabularyWord(value) || (/(^|_)code$/.test(key) && isErrorCode(value))) return termLabel(value, translate);
    return value;
  }
  return DASH;
};

const moneyTotalText = (total: Plain, translate: TranslateFunction): string =>
  `${translate('resources.insightV2.breakdown.operations', {
    smart_count: total.count as number,
    count: formatCount(total.count as number),
  })} · ${formatMinorMoney(total.amount_minor as number, typeof total.currency === 'string' ? total.currency : undefined)}`;

const stepEntryText = (entry: Plain, translate: TranslateFunction): string => {
  const cases = translate('resources.insightV2.breakdown.cases', { count: formatCount(Number(entry.n ?? 0)) });
  if ('remedy' in entry) {
    return `${translate(`resources.insightV2.remedies.${String(entry.remedy).replace(/-/g, '_')}`, { _: DASH })} (${cases})`;
  }
  if ('rate' in entry) return `${formatV2Share(entry.rate)} (${cases})`;
  const median = formatV2Duration(entry.median_seconds);
  if (!('p90_seconds' in entry)) return `${median} (${cases})`;
  const p90 = translate('resources.insightV2.breakdown.ninetyOf', { value: formatV2Duration(entry.p90_seconds) });
  return `${median} · ${p90} (${cases})`;
};

const allStepKinds = (keys: string[]): boolean => keys.length > 0 && keys.every((k) => STEP_KIND_ORDER.includes(k));

const listItems = (metricId: string, value: Plain, translate: TranslateFunction, locale: string, depth = 0): MetricListItem[] => {
  // A money total states its currency (D60): it formats the amounts, it is not a row of its own.
  const currency = typeof value.currency === 'string' ? value.currency : undefined;
  const keys = Object.keys(value).filter((key) => !(key === 'currency' && currency));
  const items: MetricListItem[] = keys.map((key) => {
    const entry = value[key];
    const label = entryLabel(metricId, key, translate, locale);
    if (isMoneyTotal(entry)) return { key, label, value: moneyTotalText(entry, translate) };
    if (isStepEntry(entry)) return { key, label, value: stepEntryText(entry, translate) };
    if (isPlainObject(entry)) {
      return depth === 0
        ? { key, label, children: listItems(metricId, entry, translate, locale, depth + 1) }
        : { key, label, value: DASH };
    }
    return { key, label, value: formatField(metricId, key, entry, translate, currency) };
  });

  if (allStepKinds(keys)) return items.sort((a, b) => stepKindRank(a.key) - stepKindRank(b.key));
  if (keys.every((k) => typeof value[k] === 'number') && keys.every((k) => !/_share$|amount_minor$|count$/.test(k))) {
    return items.sort((a, b) => (value[b.key] as number) - (value[a.key] as number));
  }
  return items;
};

const STEP_OUTCOME_ORDER = ['completed', 'skipped', 'backtracked', 'abandoned_here', 'failed', 'sin_resultado'];

/** `ops.step_funnel` — `{ "step/outcome": n }` as one row per step, one column per outcome. */
const stepFunnelTable = (value: Plain, translate: TranslateFunction): MetricView => {
  const counts = new Map<string, Map<string, number>>();
  const outcomes = new Set<string>();
  Object.entries(value).forEach(([key, n]) => {
    const [step, outcome = 'sin_resultado'] = key.split('/');
    outcomes.add(outcome);
    if (!counts.has(step)) counts.set(step, new Map());
    counts.get(step)!.set(outcome, Number(n));
  });
  const orderedOutcomes = [...outcomes].sort((a, b) => {
    const ra = STEP_OUTCOME_ORDER.indexOf(a) === -1 ? STEP_OUTCOME_ORDER.length : STEP_OUTCOME_ORDER.indexOf(a);
    const rb = STEP_OUTCOME_ORDER.indexOf(b) === -1 ? STEP_OUTCOME_ORDER.length : STEP_OUTCOME_ORDER.indexOf(b);
    return ra - rb;
  });
  const steps = [...counts.keys()].sort((a, b) => stepKindRank(a) - stepKindRank(b));

  return {
    kind: 'table',
    columns: [
      { key: 'step', label: translate('resources.insightV2.breakdown.step'), align: 'left' },
      ...orderedOutcomes.map((o) => ({ key: o, label: termLabel(o, translate), align: 'right' as const })),
    ],
    rows: steps.map((step) => ({
      key: step,
      cells: {
        step: termLabel(step, translate),
        ...Object.fromEntries(
          orderedOutcomes.map((o) => {
            const n = counts.get(step)!.get(o);
            return [o, n === undefined ? DASH : formatCount(n)];
          })
        ),
      },
    })),
  };
};

/** A list-shaped value (`it.silent_devices`, `it.peripheral_faults`...) as a table. */
const rowsTable = (metricId: string, rows: unknown[], translate: TranslateFunction): MetricView => {
  const objects = rows.filter(isPlainObject);
  const keys = [...new Set(objects.flatMap((row) => Object.keys(row)))];
  return {
    kind: 'table',
    columns: keys.map((key) => ({
      key,
      label: fieldLabel(key, translate),
      align: objects.every((row) => typeof row[key] === 'number' || row[key] === null) ? 'right' : 'left',
    })),
    rows: objects.map((row, index) => ({
      key: String(index),
      cells: Object.fromEntries(keys.map((key) => [key, formatField(metricId, key, row[key], translate)])),
    })),
  };
};

/**
 * The line under an absent figure: its remedies in words. Insufficient sample
 * (R-N) states the count it had (product decision P04: «Muestra insuficiente»
 * with the count visible), for every v2 figure — Operación, Ajuste de producto…
 */
export const stampRemedyText = (
  stamp: Pick<MetricStampDto, 'remedies' | 'sampleSize' | 'ingestGapMinutes'>,
  translate: TranslateFunction,
): string =>
  stamp.remedies
    .map((code) =>
      code === 'R-N' && typeof stamp.sampleSize === 'number' && stamp.sampleSize > 0
        ? translate('resources.insightV2.stamp.insufficientSample', {
            smart_count: stamp.sampleSize,
            count: formatCount(stamp.sampleSize),
          })
        : translate(`resources.insightV2.remedies.${code.replace(/-/g, '_')}`, { minutes: stamp.ingestGapMinutes }),
    )
    .join(' ');

/** A metric's name in the reader's language, by its stable id; the API's Spanish label only when the id has none. */
export const metricLabel = (metric: Pick<MetricResultDto, 'id' | 'label'>, translate: TranslateFunction): string =>
  translate(`resources.insightV2.metrics.${metric.id}`, { _: metric.label });

export const buildMetricView = (metric: MetricResultDto, translate: TranslateFunction, locale = 'es'): MetricView => {
  const { id, value } = metric;
  if (metric.absent || value === null || value === undefined) return { kind: 'scalar', text: DASH };
  if (Array.isArray(value)) {
    return value.length === 0 ? { kind: 'scalar', text: DASH } : rowsTable(id, value, translate);
  }
  if (!isPlainObject(value)) return { kind: 'scalar', text: formatV2Cell(id, value) };
  if (Object.keys(value).length === 0) return { kind: 'scalar', text: DASH };
  if (id === 'ops.step_funnel') return stepFunnelTable(value, translate);
  return { kind: 'list', items: listItems(id, value, translate, locale) };
};

const listText = (items: MetricListItem[]): string =>
  items
    .map((item) => (item.children ? `${item.label}: ${listText(item.children)}` : `${item.label}: ${item.value}`))
    .join('; ');

/** The same view as one line of text, for the CSV export. */
export const metricViewText = (view: MetricView): string => {
  if (view.kind === 'scalar') return view.text === DASH ? '' : view.text;
  if (view.kind === 'list') return listText(view.items);
  const [first, ...rest] = view.columns;
  return view.rows
    .map((row) => `${row.cells[first.key]}: ${rest.map((c) => `${c.label} ${row.cells[c.key]}`).join(', ')}`)
    .join('; ');
};

/** A metric's value for the CSV export — the same text the panel shows. */
export const metricExportText = (metric: MetricResultDto, translate: TranslateFunction, locale = 'es'): string =>
  metricViewText(buildMetricView(metric, translate, locale));
